import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { deal, shuffle, standardDeck, type Card, type Suit } from '@campfire/cards';
import { followable, nextSeat, newTable, partnerOf, playCard, sortHand, teamOf, turnOf, type CompletedTrick, type Trick, type TrickRules, type TrickTable } from '@campfire/tricks';

export interface EuchreOptions {
  /** Points a team needs to win. */
  readonly target: number;
  /** If everyone passes twice the dealer must name trump (no redeal). */
  readonly stickDealer: boolean;
}

export type EuchrePhase = 'order' | 'call' | 'discard' | 'play';

export type EuchreEvent =
  | { readonly kind: 'redeal'; readonly hand: number }
  | { readonly kind: 'hand'; readonly hand: number; readonly makers: 0 | 1; readonly tricks: number; readonly points: number; readonly to: 0 | 1; readonly alone: boolean; readonly euchred: boolean };

export interface EuchreState {
  readonly options: EuchreOptions;
  readonly phase: EuchrePhase;
  readonly hand: number;
  readonly dealer: Seat;
  readonly upCard: Card;
  /** The rest of the 24-card deck, face down. */
  readonly kitty: readonly Card[];
  /** Passes so far in this bidding round. */
  readonly passes: number;
  readonly trump: Suit | null;
  readonly maker: Seat | null;
  readonly alone: boolean;
  /** The dealer's face-down discard after picking up. */
  readonly discarded: Card | null;
  readonly table: TrickTable;
  /** Team scores [seats 0 & 2, seats 1 & 3]. */
  readonly scores: readonly [number, number];
  readonly last: EuchreEvent | null;
  readonly result: Outcome | null;
}

export type EuchreAction =
  | { readonly type: 'order'; readonly alone?: boolean }
  | { readonly type: 'call'; readonly suit: Suit; readonly alone?: boolean }
  | { readonly type: 'pass' }
  | { readonly type: 'discard'; readonly card: string }
  | { readonly type: 'play'; readonly card: string };

export interface EuchreView {
  readonly you: Seat | null;
  readonly phase: EuchrePhase;
  readonly hand: readonly Card[] | null;
  readonly legal: readonly string[];
  /** Suits you may name in round two. */
  readonly callSuits: readonly Suit[];
  /** You may pass now (false when the dealer is stuck). */
  readonly canPass: boolean;
  readonly handCounts: readonly number[];
  readonly dealer: Seat;
  /** The turned-up card while bidding (round two: the suit that was turned down). */
  readonly upCard: Card | null;
  readonly trump: Suit | null;
  readonly maker: Seat | null;
  readonly alone: boolean;
  /** The lone maker's partner, who sits this hand out. */
  readonly sittingOut: Seat | null;
  readonly turn: Seat | null;
  readonly trick: Trick;
  readonly lastTrick: CompletedTrick | null;
  readonly won: readonly number[];
  readonly scores: readonly [number, number];
  readonly target: number;
  readonly handNo: number;
  readonly last: EuchreEvent | null;
  readonly result: Outcome | null;
}

export const EUCHRE_RANKS = ['9', '10', 'J', 'Q', 'K', 'A'] as const;
const SUITS4: readonly Suit[] = ['C', 'D', 'H', 'S'];
const SAME_COLOUR: Readonly<Record<Suit, Suit>> = { C: 'S', S: 'C', D: 'H', H: 'D' };

/** The 24-card Euchre deck: 9 through ace in each suit (FR-EUCHRE-01). */
export function euchreDeck(): Card[] {
  return standardDeck().filter((c) => (EUCHRE_RANKS as readonly string[]).includes(c.rank));
}

/** Ranking with bowers: the jack of trumps is highest, the other jack of its colour second and counts as a trump (FR-EUCHRE-04). */
export function euchreRules(trump: Suit | null): TrickRules {
  if (trump === null) return { trump: null };
  const isRight = (c: Card) => c.rank === 'J' && c.suit === trump;
  const isLeft = (c: Card) => c.rank === 'J' && c.suit === SAME_COLOUR[trump];
  return {
    trump,
    suitOf: (c) => (isLeft(c) ? trump : (c.suit as Suit)),
    power: (c) => (isRight(c) ? 20 : isLeft(c) ? 19 : EUCHRE_RANKS.indexOf(c.rank as '9')),
  };
}

function newHand(options: EuchreOptions, hand: number, dealer: Seat, scores: readonly [number, number], rng: Rng, last: EuchreEvent | null): EuchreState {
  const { hands, remaining } = deal(shuffle(euchreDeck(), rng), 4, { perHand: 5, startHand: (dealer + 1) % 4 });
  const rules = euchreRules(null);
  return {
    options,
    phase: 'order',
    hand,
    dealer,
    upCard: remaining[0] as Card,
    kitty: remaining.slice(1),
    passes: 0,
    trump: null,
    maker: null,
    alone: false,
    discarded: null,
    table: newTable(hands.map((h) => sortHand(h, rules)), (dealer + 1) % 4),
    scores,
    last,
    result: null,
  };
}

const sittingOut = (s: EuchreState): Seat | null => (s.alone && s.maker !== null ? partnerOf(s.maker) : null);
const sitsOut = (s: EuchreState) => (seat: Seat) => seat === sittingOut(s);
const bidder = (s: EuchreState): Seat => (s.dealer + 1 + s.passes) % 4;

function actor(s: EuchreState): Seat {
  if (s.phase === 'order' || s.phase === 'call') return bidder(s);
  if (s.phase === 'discard') return s.dealer;
  return turnOf(s.table, sitsOut(s));
}

const stuck = (s: EuchreState) => s.phase === 'call' && s.options.stickDealer && bidder(s) === s.dealer;

export function callSuits(s: EuchreState): Suit[] {
  return SUITS4.filter((x) => x !== s.upCard.suit);
}

export function legalCards(s: EuchreState, seat: Seat): Card[] {
  return followable(s.table.hands[seat] ?? [], s.table.trick, euchreRules(s.trump));
}

/** Trump is set: re-sort hands with bowers, and start play left of the dealer. */
function makeTrump(s: EuchreState, trump: Suit, maker: Seat, alone: boolean, pickUp: boolean): EuchreState {
  const st: EuchreState = { ...s, trump, maker, alone };
  const out = sittingOut(st);
  const rules = euchreRules(trump);
  const dealerPicks = pickUp && out !== s.dealer;
  const hands = s.table.hands.map((h, i) => sortHand(dealerPicks && i === s.dealer ? [...h, s.upCard] : h, rules));
  return {
    ...st,
    phase: dealerPicks ? 'discard' : 'play',
    passes: 0,
    table: newTable(hands, nextSeat(s.dealer, 4, (x) => x === out)),
  };
}

function endHand(s: EuchreState, rng: Rng): EuchreState {
  const makers = teamOf(s.maker as Seat);
  const tricks = s.table.won.reduce((n, w, i) => (teamOf(i) === makers ? n + w : n), 0);
  const euchred = tricks < 3;
  const points = euchred ? 2 : tricks === 5 ? (s.alone ? 4 : 2) : 1;
  const to = euchred ? ((1 - makers) as 0 | 1) : makers;
  const scores: [number, number] = [s.scores[0], s.scores[1]];
  scores[to] += points;
  const last: EuchreEvent = { kind: 'hand', hand: s.hand, makers, tricks, points, to, alone: s.alone, euchred };
  if (scores[to] >= s.options.target) {
    return { ...s, scores, last, result: { winners: [to, to + 2], draw: false, reason: 'target', scores: { 0: scores[0], 1: scores[1], 2: scores[0], 3: scores[1] } } };
  }
  return newHand(s.options, s.hand + 1, (s.dealer + 1) % 4, scores, rng, last);
}

export const euchre: GameDefinition<EuchreState, EuchreAction, EuchreView, EuchreOptions> = {
  id: 'euchre',
  name: 'Euchre',
  rules: [
    'Four players in two teams: seats 1 and 3 against seats 2 and 4, partners sitting across. Win tricks, with a trump suit decided by bidding.',
    'The 24-card deck (9 to ace) is dealt 5 each, and the top of the remaining cards is turned up.',
    'Bidding, round one: starting left of the dealer, each player either passes or orders the turned-up suit as trump. If they do, the dealer picks the card up and discards one.',
    'Bidding, round two: if all four pass, the turned-up suit is dead and players may name any other suit, or pass again.',
    'Whoever names trump may also go alone: their partner sits out and the hand is played three-handed.',
    'Trump ranking: the jack of trump (right bower) is highest, then the other jack of the same colour (left bower, which counts as a trump), then A, K, Q, 10, 9. You must follow suit if you can.',
    'Scoring: makers who win 3 or 4 tricks score 1; all 5 tricks scores 2 (4 if going alone). Makers who win fewer than 3 are euchred and the other team scores 2.',
    'Settings: Game length (to 10 or 5 points) and Stick the dealer. If everyone passes twice, the default is a redeal; with Stick the dealer on, the dealer must name trump.',
    'The app enforces following suit and the bower rules. Table talk and signalling to your partner are on the honour system.',
  ],
  minSeats: 4,
  maxSeats: 4,
  defaultOptions: { target: 10, stickDealer: false },
  optionSpecs: [
    {
      key: 'target',
      label: 'Game length',
      choices: [
        { value: 10, label: 'To 10 points' },
        { value: 5, label: 'To 5 points' },
      ],
    },
    {
      key: 'stickDealer',
      label: 'Stick the dealer',
      help: 'When everyone passes twice.',
      choices: [
        { value: false, label: 'Off: throw in and redeal' },
        { value: true, label: 'On: the dealer must name trump' },
      ],
    },
  ],

  setup({ options, rng }) {
    return newHand(options, 1, 0, [0, 0], rng, null);
  },

  activeSeats: (s) => (s.result ? [] : [actor(s)]),

  validate(s, a, seat) {
    if (seat !== actor(s)) return { ok: false, reason: 'not your turn' };
    switch (a.type) {
      case 'order':
        return s.phase === 'order' ? { ok: true } : { ok: false, reason: 'you cannot order up now' };
      case 'call':
        if (s.phase !== 'call') return { ok: false, reason: 'you cannot name trump now' };
        return callSuits(s).includes(a.suit) ? { ok: true } : { ok: false, reason: 'name a suit other than the one turned down' };
      case 'pass':
        if (s.phase !== 'order' && s.phase !== 'call') return { ok: false, reason: 'you cannot pass now' };
        return stuck(s) ? { ok: false, reason: 'the dealer is stuck: name trump' } : { ok: true };
      case 'discard':
        if (s.phase !== 'discard') return { ok: false, reason: 'nothing to discard' };
        return (s.table.hands[seat] ?? []).some((c) => c.id === a.card) ? { ok: true } : { ok: false, reason: 'you do not hold that card' };
      case 'play':
        if (s.phase !== 'play') return { ok: false, reason: 'trump has not been named' };
        if (!(s.table.hands[seat] ?? []).some((c) => c.id === a.card)) return { ok: false, reason: 'you do not hold that card' };
        return legalCards(s, seat).some((c) => c.id === a.card) ? { ok: true } : { ok: false, reason: 'you must follow suit (the left bower is a trump)' };
      default:
        return { ok: false, reason: 'unknown action' };
    }
  },

  apply(s, a, { rng }) {
    const seat = actor(s);
    switch (a.type) {
      case 'order':
        return makeTrump(s, s.upCard.suit as Suit, seat, !!a.alone, true);
      case 'call':
        return makeTrump(s, a.suit, seat, !!a.alone, false);
      case 'pass': {
        if (s.passes < 3) return { ...s, passes: s.passes + 1 };
        if (s.phase === 'order') return { ...s, phase: 'call', passes: 0 };
        return newHand(s.options, s.hand + 1, (s.dealer + 1) % 4, s.scores, rng, { kind: 'redeal', hand: s.hand });
      }
      case 'discard': {
        const card = (s.table.hands[seat] as readonly Card[]).find((c) => c.id === a.card) as Card;
        const hands = s.table.hands.map((h, i) => (i === seat ? h.filter((c) => c.id !== a.card) : h));
        return { ...s, phase: 'play', discarded: card, table: { ...s.table, hands } };
      }
      default: {
        const { table, completed } = playCard(s.table, seat, a.card, euchreRules(s.trump), s.alone ? 3 : 4);
        const st: EuchreState = { ...s, table };
        return completed && table.won.reduce((x, y) => x + y, 0) === 5 ? endHand(st, rng) : st;
      }
    }
  },

  view(s, viewer: Viewer): EuchreView {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const turn = s.result ? null : actor(s);
    const mine = you !== null && you === turn;
    const bidding = s.phase === 'order' || s.phase === 'call';
    return {
      you,
      phase: s.phase,
      hand: you === null ? null : (s.table.hands[you] ?? null),
      legal: !mine ? [] : s.phase === 'play' ? legalCards(s, you).map((c) => c.id) : s.phase === 'discard' ? (s.table.hands[you] ?? []).map((c) => c.id) : [],
      callSuits: mine && s.phase === 'call' ? callSuits(s) : [],
      canPass: mine && bidding && !stuck(s),
      handCounts: s.table.hands.map((h) => h.length),
      dealer: s.dealer,
      upCard: bidding ? s.upCard : null,
      trump: s.trump,
      maker: s.maker,
      alone: s.alone,
      sittingOut: sittingOut(s),
      turn,
      trick: s.table.trick,
      lastTrick: s.table.lastTrick,
      won: s.table.won,
      scores: s.scores,
      target: s.options.target,
      handNo: s.hand,
      last: s.last,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result || seat !== actor(s)) return [];
    switch (s.phase) {
      case 'order':
        return [{ type: 'pass' }, { type: 'order' }, { type: 'order', alone: true }];
      case 'call': {
        const calls: EuchreAction[] = callSuits(s).flatMap((suit) => [{ type: 'call', suit }, { type: 'call', suit, alone: true }] as const);
        return stuck(s) ? calls : [{ type: 'pass' }, ...calls];
      }
      case 'discard':
        return (s.table.hands[seat] ?? []).map((c) => ({ type: 'discard', card: c.id }));
      default:
        return legalCards(s, seat).map((c) => ({ type: 'play', card: c.id }));
    }
  },
};
